import { useEffect, useRef, useState } from "react";
import { ArrowRight, RotateCcw, ShieldCheck, Banknote, Gauge, Calculator } from "lucide-react";
import { ProviderAppTile } from "./ProviderAppTile";
import { ProviderRail, INSURIFY_AUTO_URL } from "./ProviderRail";
import { VERIFIED_INSURERS } from "../lib/verifiedInsurers";
import { useQuoteModal } from "../lib/quoteModal";
import { track, events } from "../lib/analytics";

// Homepage "Compare → Understand → Save" demo. Everything here is an
// ILLUSTRATION: no insurer is contacted and no price is shown. The real
// action is the existing quote modal (→ Insurify), unchanged.
const STEP_MS = 450;
const PATHS = [
  { Icon: ShieldCheck, label: "Insurance", active: true },
  { Icon: Banknote, label: "Loans & refinancing" },
  { Icon: Gauge, label: "Ownership costs" },
  { Icon: Calculator, label: "Free calculators" },
];
const LEVELS = [
  { key: "lower", title: "Lower cost", body: "Keeps costs down, generally with higher deductibles or lower limits." },
  { key: "balanced", title: "Balanced", body: "Balances what you pay with how much you're protected.", pick: true },
  { key: "more", title: "More protection", body: "Stronger coverage, generally at a higher cost." },
];

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function useSequence(ref) {
  const total = VERIFIED_INSURERS.length;
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(0);
  useEffect(() => {
    if (reducedMotion()) { setStep(total + 1); return undefined; }
    let timer;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      timer = setInterval(() => setStep((s) => (s > total ? (clearInterval(timer), s) : s + 1)), STEP_MS);
    }, { threshold: 0.35 });
    if (ref.current) io.observe(ref.current);
    return () => { io.disconnect(); clearInterval(timer); };
  }, [ref, run, total]);
  const replay = () => { setStep(0); setRun((r) => r + 1); };
  return { step, total, done: step > total, replay };
}

const tileState = (i, step) => (i < step - 1 ? "complete" : i === step - 1 ? "active" : "resting");

const LevelCard = ({ level, shown }) => (
  <div data-testid={`comparison-level-${level.key}`} className={`border px-4 py-3.5 transition-[opacity,transform] duration-300 ${shown ? "opacity-100 translate-y-0" : "opacity-50 translate-y-1"} ${level.pick ? "border-[#0A2540]/50 bg-white" : "border-slate-200 bg-white/70"}`}>
    <p className="font-display font-black text-base text-slate-950">{level.title}</p>
    <p className="text-[13px] text-slate-600 leading-snug mt-1">{level.body}</p>
  </div>
);

export default function ComparisonExperience() {
  const demoRef = useRef(null);
  const { step, total, done, replay } = useSequence(demoRef);
  const { open } = useQuoteModal();
  const shown = Math.min(Math.max(step, 0), total);
  const cta = () => {
    track(events.HERO_CTA_CLICK, { page_type: "home_comparison_experience", vertical: "insurance" });
    open("insurance");
  };
  return (
    <section data-testid="home-comparison-experience" aria-labelledby="cmp-exp-h" className="bg-[#FAFAFA] border-b border-slate-200">
      <div className="max-w-wide mx-auto px-6 lg:px-10 pt-14 pb-10 lg:pt-20 lg:pb-14">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-brand-emerald-strong mb-3">How comparing works</p>
            <h2 id="cmp-exp-h" className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-slate-950">
              One search. More ways to save.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Compare options across insurance, financing and the cost of owning your car — without doing all the research yourself.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-2" data-testid="comparison-paths">
              {PATHS.map(({ Icon, label, active }) => (
                <li key={label} data-testid={active ? "comparison-path-active" : undefined} className={`flex items-center gap-2 border px-3 py-2.5 text-[13px] sm:text-sm leading-tight ${active ? "border-[#10B981] bg-[#10B981]/10 font-bold text-slate-950" : "border-slate-200 bg-white font-semibold text-slate-700"}`}>
                  <Icon className={`w-4 h-4 shrink-0 ${active ? "text-brand-emerald-dark" : "text-brand-emerald-strong"}`} strokeWidth={active ? 2.5 : 2} aria-hidden="true" /> {label}
                  {active && <span className="sr-only">(shown below)</span>}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-slate-600">
              Let&apos;s start with car insurance. Here&apos;s how comparing options works.
            </p>
          </div>

          <div ref={demoRef} className="lg:col-span-8 min-w-0 bg-white border border-slate-200/70" data-testid="comparison-demo">
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 pt-5">
              <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600 border border-slate-300 px-2 py-0.5" data-testid="comparison-illustrative-badge">
                Illustrative example
              </span>
              <button type="button" onClick={replay} data-testid="comparison-replay-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-950 transition-colors">
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Replay
              </button>
            </div>
            <div className="px-5 sm:px-6 pt-4">
              <div className="flex items-baseline justify-between text-sm" aria-live="polite">
                <p className="font-semibold text-slate-900" data-testid="comparison-status">
                  {done ? "Done. Here's how options usually differ." : "Exploring comparison options…"}
                </p>
                <p className="text-xs text-slate-500 tabular-nums shrink-0" data-testid="comparison-count">{shown} / {total}<span className="hidden sm:inline"> in this example</span></p>
              </div>
              <div className="h-1 bg-slate-100 mt-2" aria-hidden="true">
                <div className="h-full bg-[#10B981] transition-[width] duration-300 ease-out" style={{ width: `${(shown / total) * 100}%` }} />
              </div>
            </div>
            <ul className="grid grid-cols-4 gap-x-2 gap-y-5 sm:gap-x-5 px-4 sm:px-8 py-7" aria-label="Insurers shown in this example">
              {VERIFIED_INSURERS.map((ins, i) => (
                <li key={ins.slug} className="w-full flex justify-center"><ProviderAppTile insurer={ins} state={tileState(i, done ? total + 1 : step)} /></li>
              ))}
            </ul>
            <div className="border-t border-slate-200/70 bg-[#FAFAFA] px-5 sm:px-6 py-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Three ways to set up coverage — examples, not quotes</p>
              <div className="grid sm:grid-cols-3 gap-2 sm:gap-3" data-testid="comparison-levels">
                {LEVELS.map((l) => <LevelCard key={l.key} level={l} shown={done} />)}
              </div>
              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <p className="text-xs text-slate-500 leading-relaxed max-w-md" data-testid="comparison-disclaimer">
                  Illustration only — no insurers were contacted and no prices are shown. Availability varies by driver, vehicle, location and eligibility.
                </p>
                <button type="button" onClick={cta} data-testid="comparison-cta-btn" className="press shrink-0 inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold h-12 px-6 text-sm transition-colors">
                  Compare real quotes <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <ProviderRail href={INSURIFY_AUTO_URL} testId="comparison-insurer-rail" />
      </div>
    </section>
  );
}
